/**
 * ReportTimeline.js — Attack timeline + event frequency
 */
import React, { useMemo } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { SEVERITY_COLORS, parseTimestamp } from "../reportUtils";

export default function ReportTimeline({ data, stats, theme, SEVERITY_COLORS: SC }) {
  if (!stats || !data.length) return null;

  const timelineData = useMemo(() => {
    if (!stats.timeCol) return [];
    const buckets = {};
    data.forEach(row => {
      try {
        const d = parseTimestamp(row[stats.timeCol]);
        if (!d) return;
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")} ${String(d.getHours()).padStart(2,"0")}:00`;
        if (!buckets[key]) buckets[key] = { time: key, critical:0, high:0, medium:0, low:0, total:0 };
        const label = stats.attackCol ? String(row[stats.attackCol]||"").toLowerCase() : "";
        const sev   = label.includes("dos")||label.includes("exploit")||label.includes("inject") ? "critical"
                    : label.includes("scan")||label.includes("brute") ? "high"
                    : label.includes("normal")||label.includes("benign") ? "low"
                    : "medium";
        buckets[key][sev]++;
        buckets[key].total++;
      } catch {}
    });
    return Object.values(buckets).sort((a,b) => a.time.localeCompare(b.time)).slice(-48);
  }, [data, stats]);

  const hasTimeline = timelineData.length > 1;

  return (
    <div className="report-section page-break" style={{ marginBottom: 24 }}>
      <SectionTitle color="#6A1B9A">Attack Timeline</SectionTitle>

      {!hasTimeline ? (
        <div style={{ padding: 20, background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, fontFamily: "monospace", fontSize: 12, color: theme.colors.textMuted, textAlign: "center" }}>
          No timestamp column detected — timeline requires a date/time column in the dataset
        </div>
      ) : (
        <div className="report-card chart-container" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20 }}>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 14 }}>
            EVENT FREQUENCY OVER TIME — {timelineData.length} time buckets
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={timelineData} margin={{ top: 5, right: 20, bottom: 60, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.cardBorder} />
              <XAxis dataKey="time" tick={{ fontSize: 9, fill: theme.colors.textMuted }} angle={-45} textAnchor="end" interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: theme.colors.textMuted }} />
              <Tooltip contentStyle={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 8, fontSize: 12 }} />
              <Legend />
              <Area type="monotone" dataKey="critical" stackId="1" stroke={SC.critical} fill={SC.critical} fillOpacity={0.7} name="Critical" />
              <Area type="monotone" dataKey="high"     stackId="1" stroke={SC.high}     fill={SC.high}     fillOpacity={0.6} name="High" />
              <Area type="monotone" dataKey="medium"   stackId="1" stroke={SC.medium}   fill={SC.medium}   fillOpacity={0.5} name="Medium" />
              <Area type="monotone" dataKey="low"      stackId="1" stroke={SC.low}      fill={SC.low}      fillOpacity={0.4} name="Low" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Peak activity summary */}
      {hasTimeline && (() => {
        const peak = timelineData.reduce((max, d) => d.total > (max?.total||0) ? d : max, null);
        const quiet = timelineData.reduce((min, d) => d.total < (min?.total||Infinity) ? d : min, null);
        return (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
            <MetricCard label="PEAK ACTIVITY" value={peak?.time || "—"} sub={`${peak?.total||0} events`} color={SC.critical} theme={theme} />
            <MetricCard label="QUIETEST PERIOD" value={quiet?.time || "—"} sub={`${quiet?.total||0} events`} color={SC.low} theme={theme} />
            <MetricCard label="TIME BUCKETS ANALYZED" value={timelineData.length} sub="hourly intervals" color="#6A1B9A" theme={theme} />
          </div>
        );
      })()}
    </div>
  );
}

const MetricCard = ({ label, value, sub, color, theme }) => (
  <div style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 8, padding: "12px 16px" }}>
    <div style={{ fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted, letterSpacing: 1 }}>{label}</div>
    <div style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
    <div style={{ fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted }}>{sub}</div>
  </div>
);

const SectionTitle = ({ children, color }) => (
  <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 900, color, letterSpacing: 2, textTransform: "uppercase", borderLeft: `4px solid ${color}`, paddingLeft: 12, marginBottom: 16 }}>{children}</div>
);
